import { useState, useEffect } from 'react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { Plus, Edit, Users, UserCheck, UserX, Shield } from 'lucide-react';

const roleLabels = {
  super_admin: 'Super Admin',
  org_admin: 'Admin',
  inventory_manager: 'Inventory Manager',
  warehouse_staff: 'Warehouse Staff',
  auditor: 'Auditor'
};

const roleColors = {
  super_admin: 'bg-[#AF52DE]/10 text-[#AF52DE]',
  org_admin: 'bg-[#007AFF]/10 text-[#007AFF]',
  inventory_manager: 'bg-[#34C759]/10 text-[#34C759]',
  warehouse_staff: 'bg-[#FF9500]/10 text-[#FF9500]',
  auditor: 'bg-[#0A0A0A]/10 text-[#0A0A0A]/60'
};

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    display_name: '',
    role: 'warehouse_staff'
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        await api.put(`/users/${editingUser.id}`, {
          first_name: formData.first_name,
          last_name: formData.last_name,
          display_name: formData.display_name || null,
          role: formData.role,
          is_active: formData.is_active
        });
        toast.success('User updated');
      } else {
        await api.post('/users', formData);
        toast.success('User created');
      }
      setDialogOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      password: '',
      first_name: user.first_name,
      last_name: user.last_name,
      display_name: user.display_name || '',
      role: user.role,
      is_active: user.is_active
    });
    setDialogOpen(true);
  };

  const toggleUserStatus = async (user) => {
    try {
      await api.put(`/users/${user.id}`, { is_active: !user.is_active });
      toast.success(user.is_active ? 'User deactivated' : 'User activated');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const resetForm = () => {
    setEditingUser(null);
    setFormData({
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      display_name: '',
      role: 'warehouse_staff'
    });
  };

  const isAdmin = currentUser?.role === 'org_admin' || currentUser?.role === 'super_admin';

  return (
    <div className="space-y-6" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-heading-black text-3xl lg:text-4xl text-[#0A0A0A] tracking-tight">USERS</h1>
          <p className="text-[#0A0A0A]/60 mt-1">Manage team members and access</p>
        </div>
        {isAdmin && (
          <Button 
            className="bg-[#007AFF] hover:bg-[#0066D6]" 
            onClick={() => { resetForm(); setDialogOpen(true); }}
            data-testid="add-user-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add User
          </Button>
        )}
      </div>

      {/* Role Legend */}
      <Card className="border border-[#E5E5EA] shadow-none">
        <CardContent className="p-4">
          <p className="text-overline text-[#0A0A0A]/50 mb-3">ROLE PERMISSIONS</p>
          <div className="flex flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <Badge className={roleColors.org_admin}>Admin</Badge>
              <span className="text-sm text-[#0A0A0A]/60">Full access</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge className={roleColors.inventory_manager}>Inventory Manager</Badge>
              <span className="text-sm text-[#0A0A0A]/60">Manage items, warehouses, transactions</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge className={roleColors.warehouse_staff}>Warehouse Staff</Badge>
              <span className="text-sm text-[#0A0A0A]/60">Process transactions</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge className={roleColors.auditor}>Auditor</Badge>
              <span className="text-sm text-[#0A0A0A]/60">Read-only access</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card className="border border-[#E5E5EA] shadow-none">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#007AFF]"></div>
            </div>
          ) : users.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <Users className="w-12 h-12 text-[#0A0A0A]/20 mb-4" />
              <p className="text-[#0A0A0A]/60">No users found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-b border-[#E5E5EA]">
                  <TableHead className="text-overline text-[#0A0A0A]/50">Name</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50">Email</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50">Role</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50">Status</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50">Joined</TableHead>
                  {isAdmin && <TableHead className="text-overline text-[#0A0A0A]/50 text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id} className="border-b border-[#E5E5EA]" data-testid={`user-row-${user.email}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#007AFF] flex items-center justify-center">
                          <span className="text-white text-sm font-medium">
                            {user.first_name?.charAt(0)}{user.last_name?.charAt(0)}
                          </span>
                        </div>
                        <span className="font-medium">{user.display_name || `${user.first_name} ${user.last_name}`}</span>
                        {user.id === currentUser?.id && (
                          <Badge variant="outline" className="text-xs">You</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-[#0A0A0A]/60">{user.email}</TableCell>
                    <TableCell>
                      <Badge className={roleColors[user.role] || roleColors.warehouse_staff}>
                        {roleLabels[user.role] || user.role}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {user.is_active ? (
                        <Badge className="bg-[#34C759]/10 text-[#34C759]">Active</Badge>
                      ) : (
                        <Badge className="bg-[#FF3B30]/10 text-[#FF3B30]">Inactive</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-[#0A0A0A]/60 text-sm">
                      {new Date(user.created_at).toLocaleDateString()}
                    </TableCell>
                    {isAdmin && (
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => handleEdit(user)}
                            disabled={user.id === currentUser?.id}
                            data-testid={`edit-user-${user.email}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => toggleUserStatus(user)}
                            disabled={user.id === currentUser?.id}
                            data-testid={`toggle-user-${user.email}`}
                          >
                            {user.is_active ? (
                              <UserX className="w-4 h-4 text-[#FF3B30]" />
                            ) : (
                              <UserCheck className="w-4 h-4 text-[#34C759]" />
                            )}
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-heading text-xl flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#007AFF]" />
              {editingUser ? 'Edit User' : 'Add New User'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>First Name *</Label>
                <Input
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  placeholder="John"
                  required
                  data-testid="user-firstname-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Last Name *</Label>
                <Input
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  placeholder="Doe"
                  required
                  data-testid="user-lastname-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Display Name <span className="text-xs text-[#0A0A0A]/40">(shown on receipts)</span></Label>
              <Input
                value={formData.display_name}
                onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                placeholder="e.g., Johnny, Cashier 1, nickname"
                data-testid="user-displayname-input"
              />
            </div>
            {!editingUser && (
              <>
                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="john@company.com"
                    required
                    data-testid="user-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Password *</Label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="••••••••"
                    required
                    data-testid="user-password-input"
                  />
                </div>
              </>
            )}
            <div className="space-y-2">
              <Label>Role *</Label>
              <Select 
                value={formData.role} 
                onValueChange={(v) => setFormData({ ...formData, role: v })}
              >
                <SelectTrigger data-testid="user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="org_admin">Admin</SelectItem>
                  <SelectItem value="inventory_manager">Inventory Manager</SelectItem>
                  <SelectItem value="warehouse_staff">Warehouse Staff</SelectItem>
                  <SelectItem value="auditor">Auditor (Read-only)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-[#007AFF] hover:bg-[#0066D6]" data-testid="save-user-btn">
                {editingUser ? 'Update' : 'Create'} User
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
